// @flow
import { useSelector } from "react-redux";

import {
  getAllModelsByInstance,
  getFirstModelByInstance,
  getActiveModelByInstance,
  modelByHref,
  keyByHref,
  allKeysByHref,
  getActiveModels,
  getAllFinishedModels,
  getApplication,
} from "../redux/_modularui/ModularUISelectors";
import Href from "../models/href/Href";
import ApplicationModel from "../models/application/ApplicationModel";

import type { ModularUIModel } from "../models/types";
import type { ReduxState } from "../redux/types";

/**
 * Returns all models in the store that are instances of the provided class
 * and match the current application locale.
 */
export const useAllModelsByInstance = <T>(
  instance: Class<T>,
): Array<ModularUIModel> => {
  return useSelector((state: ReduxState) =>
    getAllModelsByInstance(state, instance),
  );
};

/**
 * Returns the first model found in the store that is an instance of the
 * provided class and matches the current locale.
 */
export const useFirstModelByInstance = <T>(instance: Class<T>): T | null => {
  return useSelector((state: ReduxState) =>
    getFirstModelByInstance(state, instance),
  );
};

/**
 * Returns the model instance that is currently "active" based on the URL path.
 * It checks if the current router location starts with the model's selfhref path.
 */
export const useActiveModelByInstance = <T>(instance: Class<T>): T | null => {
  return useSelector((state: ReduxState) =>
    getActiveModelByInstance(state, instance),
  );
};

/**
 * Retrieves a specific model from the store based on its unique Href.
 * @param {Href | string} href - The Href object or string path of the model.
 * @returns {ModularUIModel | null} The model matching the href or null.
 */
export const useModelByHref = (href: Href | string): null | ModularUIModel => {
  return useSelector((state: ReduxState) => modelByHref(state, href));
};

/**
 * Returns the unique store key (identifier) for a model associated with a specific Href.
 * @param {Href | string} href - The Href object or string path.
 * @returns {string | null | void} The internal key of the model.
 */
export const useKeyByHref = (href: Href | string): ?string => {
  return useSelector((state: ReduxState) => keyByHref(state, href));
};

/**
 * Returns all internal keys found in the store for a specific Href.
 * Useful if multiple versions/entries exist for the same path.
 * @param {Href | string} href - The Href object or string path.
 * @returns {Array<string>} An array of keys.
 */
export const useAllKeysByHref = (href: Href | string): Array<string> => {
  return useSelector((state: ReduxState) => allKeysByHref(state, href));
};

/**
 * Returns a list of simplified model info (key, href, label, type) for all
 * models currently active in the navigation context (breadcrumbs).
 * @returns {Array<{key: string, href: Href, label: string, type: string}>}
 */
export const useActiveModels = (): Array<{
  key: string,
  href: Href,
  label: string,
  type: string,
}> => {
  return useSelector(getActiveModels);
};

/**
 * Returns all models in the store that have a status of MODULARUI_STATUS.FINISHED.
 * @returns {Array<ModularUIModel>} An array of fully loaded models.
 */
export const useAllFinishedModels = (): Array<ModularUIModel> => {
  return useSelector(getAllFinishedModels);
};

/**
 * Returns the current active application, without retrying to request it.
 */
export const useCurrentApplication = (): ApplicationModel | null => {
  const application = useSelector(getApplication);
  if (application instanceof ApplicationModel) {
    return application;
  }
  return null;
};
